import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let hasRegisteredPlugin = false;

function registerScrollTrigger() {
  if (hasRegisteredPlugin) {
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  hasRegisteredPlugin = true;
}

function getHeaderHeight() {
  var header = document.querySelector('.site-header');
  return header ? Math.round(header.getBoundingClientRect().height) : 0;
}

function dispatchHeroTakeoverEvent(eventName) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return;
  }

  window.dispatchEvent(new CustomEvent(eventName));
}

function getHeroMetrics(sectionElement, imageWrapElement) {
  var sectionRect = sectionElement.getBoundingClientRect();
  var imageRect = imageWrapElement.getBoundingClientRect();
  var heroCenterX = sectionRect.left + sectionRect.width / 2;
  var heroCenterY = sectionRect.top + sectionRect.height / 2;
  var imageCenterX = imageRect.left + imageRect.width / 2;
  var imageCenterY = imageRect.top + imageRect.height / 2;
  var viewportHeight = Math.max(1, window.innerHeight - getHeaderHeight());
  var targetWidth = Math.max(sectionRect.width, 1);
  var targetHeight = Math.max(viewportHeight, sectionRect.height);
  var targetScale = Math.max(targetWidth / Math.max(imageRect.width, 1), targetHeight / Math.max(imageRect.height, 1), 1);
  var shiftX = heroCenterX - imageCenterX;
  var shiftY = heroCenterY - imageCenterY;
  var scaleTravel = Math.max(0, targetScale - 1) * Math.max(imageRect.height, 1);
  var dynamicDistance = Math.max(window.innerHeight * 0.75, Math.abs(shiftY) + scaleTravel * 0.85);

  return {
    shiftX: shiftX,
    shiftY: shiftY,
    targetScale: targetScale,
    dynamicDistance: dynamicDistance,
  };
}

function createHeroTakeoverTimeline(sectionElement, imageWrapElement, distanceMultiplier) {
  var timeline = gsap.timeline({
    defaults: {
      ease: 'none',
    },
    scrollTrigger: {
      trigger: sectionElement,
      start: function start() {
        return 'top top+=' + getHeaderHeight();
      },
      end: function end() {
        var metrics = getHeroMetrics(sectionElement, imageWrapElement);
        return '+=' + Math.round(metrics.dynamicDistance * distanceMultiplier);
      },
      scrub: 0.35,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onToggle: function onToggle(self) {
        sectionElement.classList.toggle('home-section--hero-pinned-active', self.isActive);
      },
      onLeave: function onLeave() {
        dispatchHeroTakeoverEvent('rs:home-hero-takeover-end');
      },
      onEnterBack: function onEnterBack() {
        dispatchHeroTakeoverEvent('rs:home-hero-takeover-reset');
      },
    },
  });

  timeline.to(imageWrapElement, {
    x: function x() {
      return getHeroMetrics(sectionElement, imageWrapElement).shiftX;
    },
    y: function y() {
      return getHeroMetrics(sectionElement, imageWrapElement).shiftY;
    },
    scale: function scale() {
      return getHeroMetrics(sectionElement, imageWrapElement).targetScale;
    },
    force3D: true,
    duration: 1,
  });

  return timeline;
}

export function initializeHomeHeroScrollTakeover() {
  if (typeof window === 'undefined') {
    return function noop() {};
  }

  registerScrollTrigger();

  var sectionElement = document.querySelector('.home-section--hero');

  if (!sectionElement) {
    return function noop() {};
  }

  var imageWrapElement = sectionElement.querySelector('.home-hero__image-wrap');

  if (!imageWrapElement) {
    return function noop() {};
  }

  function updateHeaderOffsetVariable() {
    var headerHeight = getHeaderHeight();

    if (headerHeight > 0) {
      sectionElement.style.setProperty('--site-header-height', headerHeight + 'px');
    }
  }

  updateHeaderOffsetVariable();
  window.addEventListener('resize', updateHeaderOffsetVariable, { passive: true });

  gsap.set(imageWrapElement, {
    transformOrigin: '50% 50%',
    willChange: 'transform',
  });

  var mediaMatcher = gsap.matchMedia();

  mediaMatcher.add({
    reduceMotion: '(prefers-reduced-motion: reduce)',
    desktop: '(min-width: 1025px)',
    tablet: '(min-width: 768px) and (max-width: 1024px)',
    mobile: '(max-width: 767px)',
  }, function addTakeover(context) {
    var conditions = context.conditions;

    if (conditions.reduceMotion) {
      sectionElement.classList.remove('home-section--hero-animated');
      sectionElement.classList.remove('home-section--hero-pinned-active');
      gsap.set(imageWrapElement, { clearProps: 'transform,will-change' });
      return undefined;
    }

    sectionElement.classList.add('home-section--hero-animated');

    var distanceMultiplier = 1;

    if (conditions.desktop) {
      distanceMultiplier = 1.08;
    } else if (conditions.tablet) {
      distanceMultiplier = 1;
    } else if (conditions.mobile) {
      distanceMultiplier = 0.92;
    }

    var timeline = createHeroTakeoverTimeline(sectionElement, imageWrapElement, distanceMultiplier);

    return function cleanupTimeline() {
      timeline.kill();
    };
  });

  return function cleanupHeroTakeover() {
    mediaMatcher.revert();
    sectionElement.classList.remove('home-section--hero-animated');
    sectionElement.classList.remove('home-section--hero-pinned-active');
    window.removeEventListener('resize', updateHeaderOffsetVariable);
    gsap.set(imageWrapElement, { clearProps: 'transform,will-change' });
    ScrollTrigger.refresh();
  };
}
